// ============================================================================
// LE JOURNAL D'ACTIVITÉ — qui a fait quoi, quand, et depuis où, dans le magasin actif (Réglages).
//
// UTILITÉ : lire en une liste ce qui s'est passé aux caisses (sessions, espèces, ventes abandonnées), au back-office
// (objectifs) et à la Console (accès de l'équipe Orgazone), la plus récente en tête. La maquette l'appelle « Journal
// d'audit » ; ce n'est pas un audit — la preuve fiscale vit dans Santé NF525 et les Archives (CADRAGE-Journal-Activite.md).
//
// ⚠️ LE SERVEUR REND DES FAITS, L'ÉCRAN COMPOSE LA PHRASE dans la langue du compte. Les gestes sur les objectifs
//    s'écrivent avec la phrase de l'écran Objectifs (`objectiveChangeText`) : deux phrases du même geste divergeraient.
// ⚠️ LA PAGE SUIVANTE SE DEMANDE PAR SON CURSEUR (« Afficher plus ») : le serveur ne pagine pas par numéro, et une
//    réponse dépassée (un filtre changé pendant la lecture) est écartée — elle réécrirait la liste d'un autre filtre.
// ⚠️ CE QU'IL NE COUVRE PAS SE DIT : les gestes du siège sur les prix, le stock, l'inventaire, les droits, les réglages,
//    les promotions et le marketing viendront domaine par domaine ; le filtre des domaines ne propose que ceux qui écrivent.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@orgazone/money-format';
import { Badge, Button, EmptyState, FilterToolbar, Icon, Picker, Trans, showToast } from '@orgazone/ui-web';
import { abandonReasonText } from '@orgazone/sync-contract';
import {
  api, type AbandonedRow, type ActivityDomain, type ActivityParams, type ActivityRowView, type ObjectiveChangeView,
} from '../api';
import { useAuth } from '../auth';
import { codeCaisse } from '../caisse';
import { key, t, type Translatable } from '../i18n';
import { horodatageFiscal, jourYmd } from '../periode';
import { useStoreScope } from '../store-context';
import { exporterCsv } from './export-csv';
import { AbandonedSaleModal } from './journal';
import { objectiveChangeText } from './objectifs';

/** Les domaines que le serveur sert, leur nom et leur teinte — la maquette en dessine sept, seuls ceux-ci écrivent. */
const DOMAINS: Record<ActivityDomain, { label: Translatable; bg: string; fg: string }> = {
  register: { label: key('Caisse'), bg: 'var(--orange-bg)', fg: 'var(--orange-fg)' },
  objectives: { label: key('Objectifs'), bg: 'var(--green-bg)', fg: 'var(--green-fg)' },
  access: { label: key('Accès & droits'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
};

/** Les périodes offertes, comme la maquette — « toute la période » se lit par curseur, le serveur le tient. */
const PERIODS: { value: string; days: number | null; label: Translatable }[] = [
  { value: '', days: null, label: key('Toute la période') },
  { value: '30', days: 30, label: key('30 derniers jours') },
  { value: '92', days: 92, label: key('3 derniers mois') },
  { value: '183', days: 183, label: key('6 derniers mois') },
  { value: '365', days: 365, label: key('12 derniers mois') },
];

/** Cinquante lignes par lecture ; l'export lit par deux cents, jusqu'à cinq mille, et dit s'il s'arrête avant la fin. */
const PAGE = 50;
const EXPORT_PAGE = 200;
const EXPORT_MAX = 5000;

/** Les bornes d'une période glissante, aujourd'hui compris — `null` pour toute la période. */
function boundsOf(period: string): { from: string; to: string } | null {
  const p = PERIODS.find((x) => x.value === period);
  if (!p?.days) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (p.days - 1));
  return { from: jourYmd(start), to: jourYmd(today) };
}

const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const money = (v: unknown): string => (typeof v === 'number' ? formatMoney(v) : '—');

/**
 * L'action et son détail, composés dans la langue du compte d'après les faits du serveur.
 * ⚠️ UN CODE INCONNU S'AFFICHE TEL QUEL : un serveur plus récent peut en servir un nouveau, et le taire ferait disparaître
 *    une ligne qui a bien eu lieu.
 */
export function describeActivity(r: ActivityRowView): { label: string; detail: string } {
  const f = r.facts;
  const caisse = str(f.registerId) ? codeCaisse(str(f.registerId)!) : '—';
  switch (r.kind) {
    case 'session_opened': {
      const declared = num(f.floatCents);
      const carried = num(f.carriedOverCents);
      return {
        label: t('Session de caisse ouverte'),
        detail: declared !== null && carried !== null && declared !== carried
          ? t('{caisse} · fond de caisse {montant}, corrigé (calculé : {calcule})', { caisse, montant: money(declared), calcule: money(carried) })
          : t('{caisse} · fond de caisse {montant}', { caisse, montant: money(f.floatCents) }),
      };
    }
    case 'session_closed':
      return {
        label: t('Session de caisse clôturée'),
        detail: num(f.countedCents) === null
          ? t('{caisse} · aucun comptage de clôture remonté', { caisse })
          : f.uncounted === true
            ? t('{caisse} · tiroir non compté (clôture automatique)', { caisse })
            : t('{caisse} · compté {compte} · écart {ecart}', { caisse, compte: money(f.countedCents), ecart: money(f.varianceCents) }),
      };
    case 'cash_withdrawal':
      return { label: t('Prélèvement d’espèces'), detail: t('{caisse} · {montant} — {motif}', { caisse, montant: money(f.amountCents), motif: str(f.reason) ?? '—' }) };
    case 'cash_deposit':
      return { label: t('Apport d’espèces'), detail: t('{caisse} · {montant} — {motif}', { caisse, montant: money(f.amountCents), motif: str(f.reason) ?? '—' }) };
    case 'cash_expense':
      return { label: t('Dépense payée en espèces'), detail: t('{caisse} · {montant} — {motif}', { caisse, montant: money(f.amountCents), motif: str(f.reason) ?? '—' }) };
    case 'cash_movement':
      return {
        label: t('Mouvement d’espèces ({type})', { type: str(f.type) ?? '—' }),
        detail: t('{caisse} · {montant} — {motif}', { caisse, montant: money(f.amountCents), motif: str(f.reason) ?? '—' }),
      };
    case 'sale_abandoned': {
      const reason = abandonReasonText(str(f.reason));
      const values = { caisse, articles: t('{n} article', { n: num(f.lineCount) ?? 0 }), montant: money(f.estimatedCents) };
      return {
        label: reason ? t(reason) : t('Vente abandonnée'),
        detail: f.paymentStarted === true
          ? t('{caisse} · {articles} · {montant} estimés · paiement engagé', values)
          : t('{caisse} · {articles} · {montant} estimés', values),
      };
    }
    case 'access':
      return { label: t('Accès de l’équipe Orgazone'), detail: r.text ?? '' };
    default:
      if (r.kind.startsWith('objective_')) {
        const change = {
          at: r.atMs, actor: r.actor, kind: r.kind.slice('objective_'.length),
          subject: str(f.subject), beforeCents: num(f.beforeCents), afterCents: num(f.afterCents),
          changeBp: num(f.changeBp), dayKind: str(f.dayKind), count: num(f.count),
        } as ObjectiveChangeView;
        return { label: t('Objectifs de vente'), detail: objectiveChangeText(change) };
      }
      return { label: r.kind, detail: '' };
  }
}

/**
 * D'où vient une ligne — la colonne « Origine » de la maquette, nommée « Provenance » ici : « Origine » est aussi le nom
 * d'un champ article (le pays d'origine), et un homonyme prend en silence la traduction de l'autre.
 */
function originOf(r: ActivityRowView): string {
  if (r.origin.kind === 'register') return codeCaisse(r.origin.registerId);
  return r.origin.kind === 'console' ? t('Console') : t('Back-office');
}

/** Une ligne d'abandon, sous la forme que la fenêtre du panier lit. */
function abandonedRowOf(r: ActivityRowView): AbandonedRow {
  const f = r.facts;
  return {
    id: r.key.slice('sale_abandoned:'.length), voidedAtLocal: r.atLocal, registerId: str(f.registerId) ?? '',
    operatorName: r.actor, lineCount: num(f.lineCount) ?? 0, estimatedTotalInclTaxCents: num(f.estimatedCents) ?? 0,
    paymentStarted: f.paymentStarted === true, reason: str(f.reason), fromLegacy: f.fromLegacy === true,
  };
}

/** Le journal d'activité du magasin actif. */
export function Activite() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState<ActivityDomain | ''>('');
  const [actor, setActor] = useState('');
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState<ActivityRowView[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [actors, setActors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [abandon, setAbandon] = useState<AbandonedRow | null>(null);
  const [attempt, setAttempt] = useState(0);
  /** Le numéro de la lecture en cours — une réponse d'une lecture dépassée est écartée. */
  const seq = useRef(0);

  // ⚠️ LA RECHERCHE ATTEND LA FIN DE LA FRAPPE : une lecture par lettre relirait six sources à chaque touche.
  useEffect(() => {
    const id = window.setTimeout(() => setQuery(q.trim()), 300);
    return () => window.clearTimeout(id);
  }, [q]);

  const params = useCallback((before?: string, size = PAGE): ActivityParams => {
    const b = boundsOf(period);
    return {
      ...(b ?? {}), domains: domain ? [domain] : undefined, actor: actor || undefined, q: query || undefined, before, size,
    };
  }, [period, domain, actor, query]);

  useEffect(() => {
    if (!gln) return;
    const mine = ++seq.current;
    setRows(null); setError(null); setNext(null);
    api.activity(params())
      .then((p) => {
        if (mine !== seq.current) return;
        setRows(p.rows); setNext(p.next); setCount(p.count); setActors(p.actors ?? []);
      })
      .catch((e: unknown) => { if (mine === seq.current) setError(e instanceof Error ? e.message : String(e)); });
  }, [gln, params, attempt]);

  const showMore = async () => {
    if (!next || more) return;
    const mine = seq.current;
    setMore(true);
    try {
      const p = await api.activity(params(next));
      if (mine !== seq.current) return;
      setRows((cur) => [...(cur ?? []), ...p.rows]);
      setNext(p.next);
    } catch (e) {
      showToast(t('La suite du journal n’a pas pu être lue : {error}.', { error: e instanceof Error ? e.message : String(e) }), 'err');
    } finally {
      setMore(false);
    }
  };

  /**
   * L'EXPORT EMPORTE CE QUE LES FILTRES MONTRENT, page après page.
   * ⚠️ BORNÉ, ET IL LE DIT : au-delà de cinq mille lignes, le fichier s'arrête et le message le précise — un fichier
   *    tronqué sans le dire se lirait comme toute l'histoire.
   */
  const exportRows = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all: ActivityRowView[] = [];
      let before: string | undefined;
      let truncated = false;
      for (;;) {
        const p = await api.activity(params(before, EXPORT_PAGE));
        all.push(...p.rows);
        if (!p.next) break;
        if (all.length >= EXPORT_MAX) { truncated = true; break; }
        before = p.next;
      }
      exporterCsv(
        'journal-activite',
        [t('Horodatage'), t('Auteur'), t('Domaine'), t('Action'), t('Détail'), t('Provenance'), t('NF525')],
        all.map((r) => {
          const d = describeActivity(r);
          return [horodatageFiscal(r.atLocal), r.actor, t(DOMAINS[r.domain].label), d.label, d.detail, originOf(r), r.nf525 ? t('oui') : ''];
        }),
      );
      showToast(truncated
        ? t('Export limité aux {n} premières lignes — resserrez les filtres pour le reste.', { n: all.length })
        : t('Export du journal : {n} ligne', { n: all.length }), truncated ? 'err' : 'ok');
    } catch (e) {
      showToast(t('L’export n’a pas pu être fait : {error}.', { error: e instanceof Error ? e.message : String(e) }), 'err');
    } finally {
      setExporting(false);
    }
  };

  const filtered = Boolean(query || domain || actor || period);
  const reset = () => { setQ(''); setQuery(''); setDomain(''); setActor(''); setPeriod(''); };

  if (!gln) {
    return (
      <>
        <Heading onExport={null} exporting={false} />
        <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez le magasin dont vous voulez lire l’activité.')} /></div>
      </>
    );
  }

  return (
    <>
      <Heading onExport={rows && rows.length ? () => void exportRows() : null} exporting={exporting} />
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--ink-200)', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <FilterToolbar
              search={<Search value={q} onChange={setQ} />}
              actifs={(domain ? 1 : 0) + (actor ? 1 : 0) + (period ? 1 : 0)}
              title={t('Filtres du journal d’activité')}
              filters={<>
                <Picker id="bo-activite-domaine" label={t('Domaine')} allLabel={t('Tous les domaines')} value={domain}
                  onChange={(v) => setDomain(v as ActivityDomain | '')}
                  options={(Object.keys(DOMAINS) as ActivityDomain[]).map((d) => ({ value: d, label: t(DOMAINS[d].label) }))} />
                <Picker id="bo-activite-auteur" label={t('Auteur')} allLabel={t('Tous les auteurs')} value={actor} onChange={setActor}
                  // ⚠️ UN AUTEUR CHOISI RESTE OFFERT même absent de la période : sinon on ne pourrait plus le défaire.
                  options={[...new Set([...(actor ? [actor] : []), ...actors])].map((a) => ({ value: a, label: a }))} />
                <Picker id="bo-activite-periode" label={t('Période')} allLabel={t('Toute la période')} value={period} onChange={setPeriod}
                  options={PERIODS.filter((p) => p.value).map((p) => ({ value: p.value, label: t(p.label) }))} />
              </>}
            />
          </div>
          {filtered && (
            <Button size="sm" onClick={reset}>{t('Effacer')}</Button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {count === null ? '' : t('{n} événement', { n: count })}
          </span>
        </div>
        <Rows rows={rows} error={error} filtered={filtered} onReset={reset} onRetry={() => setAttempt((n) => n + 1)} onOpenAbandon={(r) => setAbandon(abandonedRowOf(r))} />
        {next && rows && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ink-100)', display: 'flex', justifyContent: 'center' }}>
            <Button size="sm" onClick={() => void showMore()} disabled={more}>{more ? t('Lecture…') : t('Afficher plus')}</Button>
          </div>
        )}
        <Footer />
      </div>
      <AbandonedSaleModal row={abandon} onClose={() => setAbandon(null)} />
    </>
  );
}

/** Le titre de l'écran, sa promesse, et l'export. */
function Heading({ onExport, exporting }: { onExport: (() => void) | null; exporting: boolean }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  return (
    <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 250 }}>
        <h1 style={{ margin: 0, fontSize: '25px', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Journal d’activité')}</h1>
        <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
          {/* ⚠️ LE LIEN VERS SANTÉ NF525 N'EXISTE QUE POUR QUI PEUT L'OUVRIR : aucun écran ne renvoie vers un module que le
              rôle ne voit pas. */}
          {can('bo.nf525.view') ? (
            <Trans
              k="Qui a fait quoi, quand, depuis où — l’activité du magasin, aux caisses et au siège. La preuve fiscale scellée vit dans <1>Santé NF525</1>."
              parts={[(x) => (
                <a href="/sante-nf525" onClick={(e) => { e.preventDefault(); navigate('/sante-nf525'); }}
                  style={{ color: 'var(--teal-800)', fontWeight: 600 }}>{x}</a>
              )]}
            />
          ) : t('Qui a fait quoi, quand, depuis où — l’activité du magasin, aux caisses et au siège. La preuve fiscale scellée vit dans Santé NF525.')}
        </p>
      </div>
      {onExport && (
        <Button icon="download" onClick={onExport} disabled={exporting}>{exporting ? t('Export en cours…') : t('Export Excel')}</Button>
      )}
    </header>
  );
}

/** Le champ de recherche — l'auteur, la caisse, le motif ou le texte d'un accès. */
function Search({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 210 }}>
      <span aria-hidden style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-400)', display: 'flex', pointerEvents: 'none' }}>
        <Icon name="search" size={16} stroke={2} />
      </span>
      <input
        className="field-input" type="search" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={t('Auteur, caisse, motif…')} aria-label={t('Rechercher dans le journal d’activité')}
        style={{ width: '100%', height: 40, padding: '0 14px 0 38px', borderRadius: 'var(--r-pill)', fontSize: '13.5px' }}
      />
    </div>
  );
}

const GRID = '118px 160px 132px minmax(0,1fr) 96px';

/** Les lignes du journal, ou l'état qui dit pourquoi il n'y en a pas. */
function Rows({ rows, error, filtered, onReset, onRetry, onOpenAbandon }: {
  rows: ActivityRowView[] | null; error: string | null; filtered: boolean; onReset: () => void; onRetry: () => void;
  onOpenAbandon: (r: ActivityRowView) => void;
}) {
  if (error) {
    return (
      <div style={{ padding: '34px 18px' }}>
        <EmptyState icon="alert" title={t('Journal illisible')} note={t('La lecture n’a pas abouti : {error}', { error })}
          action={<Button size="sm" onClick={onRetry}>{t('Réessayer')}</Button>} />
      </div>
    );
  }
  if (!rows) return <div style={{ padding: '34px 18px' }}><EmptyState icon="search" title={t('Lecture du journal…')} note={t('Les traces des caisses et du siège sont relues.')} /></div>;
  if (!rows.length) {
    return (
      <div style={{ padding: '34px 18px', textAlign: 'center' }}>
        {filtered ? (
          <>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Rien ne correspond à ces filtres')}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--ink-500)', marginTop: 4 }}>
              <Trans k="Élargissez la période ou <1>effacez les filtres</1>." parts={[(x) => (
                <button type="button" onClick={onReset} style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>{x}</button>
              )]} />
            </div>
          </>
        ) : (
          <EmptyState icon="info" title={t('Aucune activité enregistrée')} note={t('Rien n’a encore été tracé dans ce magasin : sessions, espèces, ventes abandonnées, objectifs ou accès.')} />
        )}
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <div role="table" aria-label={t('Journal d’activité')} style={{ minWidth: 760 }}>
        <div role="row" style={{ display: 'grid', gridTemplateColumns: GRID, padding: '8px 16px', borderBottom: '1px solid var(--ink-100)', fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>
          <span role="columnheader">{t('Horodatage')}</span>
          <span role="columnheader">{t('Auteur')}</span>
          <span role="columnheader">{t('Domaine')}</span>
          <span role="columnheader">{t('Action')}</span>
          <span role="columnheader">{t('Provenance')}</span>
        </div>
        {rows.map((r) => {
          const d = describeActivity(r);
          const dom = DOMAINS[r.domain];
          const opens = r.kind === 'sale_abandoned';
          return (
            <div key={r.key} role="row" style={{ display: 'grid', gridTemplateColumns: GRID, padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center' }}>
              <span role="cell" style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{horodatageFiscal(r.atLocal)}</span>
              <span role="cell" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8 }}>{r.actor || '—'}</span>
              <span role="cell">
                <span style={{ height: 20, display: 'inline-flex', alignItems: 'center', padding: '0 8px', borderRadius: 'var(--r-pill)', background: dom?.bg ?? 'var(--ink-100)', color: dom?.fg ?? 'var(--ink-600)', fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {dom ? t(dom.label) : r.domain}
                </span>
              </span>
              <span role="cell" style={{ minWidth: 0, paddingRight: 10 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {opens ? (
                    <button type="button" onClick={() => onOpenAbandon(r)} style={{ border: 'none', background: 'transparent', padding: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--teal-800)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.label}</button>
                  ) : (
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.label}</span>
                  )}
                  {r.nf525 && (
                    <span title={t('Ce fait cite sa pièce au journal fiscal.')}><Badge tone="info">{t('NF525')}</Badge></span>
                  )}
                </span>
                {d.detail && (
                  <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={d.detail}>{d.detail}</span>
                )}
              </span>
              <span role="cell" style={{ fontSize: '11px', color: 'var(--ink-400)', fontWeight: 600 }}>{originOf(r)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Ce que ce journal est, et ce qu'il couvre aujourd'hui. */
function Footer() {
  return (
    <div style={{ padding: '12px 16px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
      <Trans
        k="Journal de l’<1>activité</1>, lisible et filtrable — consultation seule, rien ne s’y modifie. Une ligne marquée NF525 cite sa pièce au journal fiscal ; la preuve légale scellée se consulte dans Santé NF525 et les Archives fiscales, jamais ici. Rien n’est purgé : la page relit les traces d’origine, et l’export emporte ce que les filtres montrent."
        parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]}
      />
      <span style={{ display: 'block', marginTop: 4 }}>
        {t('Il couvre aujourd’hui les caisses (sessions, espèces, ventes abandonnées), les objectifs et les accès de l’équipe Orgazone. Les gestes du siège sur les prix, le stock, l’inventaire, les droits, les réglages, les promotions et le marketing y entreront domaine par domaine.')}
      </span>
    </div>
  );
}
